import { Spin, Typography } from "antd";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useGetExchangeRateBulk } from "../../api/currency";
import { canCreateNewOrder } from "../../utils/canCreateNewOrder";
import { ReactComponent as TimerIcon } from "../../common/images/svg/Timer.svg";
import { ReactComponent as UsersThreeIcon } from "../../common/images/svg/UsersThree.svg";
import { ButtonMethodCustom } from "../../components/button/buttonOption";
import { CustomToggle } from "../../components/customeToggle/customToggle";
import { SearchCustom } from "../../components/inputs/search/searchCustom";
import { setTitleHeader } from "../../store/use-title-header-store";
import useHomepage from "../../utils/hooks/useHomepage";
import { useReloadSocket } from "../../utils/hooks/useReloadSocket";
import useResponsive from "../../utils/hooks/useResponsive";
import "../styles/index.css";
import Item from "./component/item";
import ModalDeposit from "./component/modal-deposits";
import ModalDetail from "./component/modal-detail";
import ModalExchange from "./component/modal-exchange";
import ModalSuccess from "./component/modal-success";
import ModalWithdraw from "./component/modal-withdraw";
import "./home.css";
import { ThemedOption } from "./option/option";

export const HomePage: React.FC<any> = () => {
  const navigate = useNavigate();
  const socket = useReloadSocket();
  const canAction = canCreateNewOrder();
  const { isTablet } = useResponsive();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const currency = queryParams.get("currency");
  const type = queryParams.get("type");
  const isValidType = !type || type === "FIAT" || type === "CRYPTO";
  const [method, setMethod] = useState<string>(type || "FIAT");
  const [currencyOption, setCurrencyOption] = useState("USD");
  const [data, setData] = useState<any[]>([]);

  const [rates, setRates] = useState({
    EUR: "1",
    ETH: "1",
    DAI: "1",
    USDT: "1",
    USD: "1",
  });

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [deposit, setDeposit] = useState<any>(null);
  const [exchange, setExchange] = useState<any>(null);
  const [withdraw, setWithdraw] = useState<any>(null);
  const [prevWithdraw, setPrevWithdraw] = useState<any>(null);
  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [responseWithdrawal, setResponseWithdrawal] = useState<any>();
  const [checked, setChecked] = useState(false);
  const [search, setSearch] = useState("");

  const {
    dataAllCrypto,
    isLoadingCrypto,
    totalAsset,
    refetchCryptoCurrency,
    refetchTotalAsset,
  } = useHomepage(search, method, isValidType);

  const { data: dataExchangeRate, refetch: refetchRate } =
    useGetExchangeRateBulk(isValidType);

  useEffect(() => {
    if (!dataExchangeRate) return;
    setRates(dataExchangeRate?.data?.data?.rates);
  }, [dataExchangeRate]);

  useEffect(() => {
    const newData = checked
      ? dataAllCrypto.filter((item) => item.totalBalance !== 0)
      : dataAllCrypto;

    const isSame =
      data.length === newData.length &&
      data.every((item, idx) => item === newData[idx]);
    if (!isSame) {
      setData(newData);
    }
  }, [dataAllCrypto, checked]);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  useEffect(() => {
    if (!isValidType) {
      navigate("/404", { replace: true });
    }
  }, [isValidType, navigate]);

  useEffect(() => {
    setTitleHeader("Portfolio");
  }, []);

  useEffect(() => {
    if (!currency) return;
    const item = data.find((item) => item?.id === Number(currency));
    setSelectedItem(item);
  }, [currency, data]);

  useEffect(() => {
    if (!type) return;
    setMethod(type);
  }, [type]);

  // useEffect(() => {
  //   socket.connect();
  //   return () => {
  //     socket.disconnect();
  //   };
  // }, [socket]);

  useEffect(() => {
    const handleWalletUpdated = () => {
      refetchCryptoCurrency();
      refetchTotalAsset();
    };
    socket.on(`user.wallet.updated`, handleWalletUpdated);

    return () => {
      socket.off(`user.wallet.updated`, handleWalletUpdated);
    };
  }, [socket, refetchCryptoCurrency, refetchTotalAsset]);

  useEffect(() => {
    const handleRateChanged = () => {
      refetchRate();
      refetchTotalAsset();
    };
    socket.on(`all.exchangeRate.update`, handleRateChanged);
    return () => {
      socket.off(`all.exchangeRate.update`, handleRateChanged);
    };
  }, [socket, refetchRate, refetchTotalAsset]);

  return (
    <div
      style={{
        height: "100%",
        minHeight: "calc(100vh - 54px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ThemedOption
        currencyOption={currencyOption}
        setCurrencyOption={setCurrencyOption}
        canAction={canAction}
        totalAsset={totalAsset}
        rates={rates}
      />
      <div className="frame-home">
        <div className={"frame-home-left"}>
          <ButtonMethodCustom
            size={isTablet ? "small" : "medium"}
            text="Fiat"
            isSelected={method === "FIAT"}
            onClick={() => {
              navigate("/home?type=FIAT");
              setMethod("FIAT");
              setSearch("");
            }}
          />
          <ButtonMethodCustom
            size={isTablet ? "small" : "medium"}
            text="Crypto"
            isSelected={method === "CRYPTO"}
            onClick={() => {
              navigate("/home?type=CRYPTO");
              setMethod("CRYPTO");
              setSearch("");
            }}
          />
          <div className={"frame-home-left-footer"}>
            <Link to="/beneficiaries">
              <div className={"frame-home-left-footer-item"}>
                <UsersThreeIcon />
                <Typography
                  style={{
                    fontSize: isTablet ? 10 : 12,
                    fontWeight: 500,
                    color: "white",
                    fontFamily: "Inter",
                  }}
                >
                  {"Beneficiaries & Remitters"}
                </Typography>
              </div>
            </Link>
            <Link to="/history">
              <div className={"frame-home-left-footer-item"}>
                <TimerIcon />
                <Typography
                  style={{
                    fontSize: isTablet ? 10 : 12,
                    fontWeight: 500,
                    color: "white",
                    fontFamily: "Inter",
                  }}
                >
                  {"Transactions / History"}
                </Typography>
              </div>
            </Link>
          </div>
        </div>
        <div className={"frame-home-right"}>
          <div className={"frame-home-right-header"}>
            <SearchCustom
              value={search}
              placeholder="Search"
              onChange={handleSearch}
              onSubmit={handleSearch}
            />
            <CustomToggle
              checked={checked}
              onChange={setChecked}
              content="Hide zero balances"
            />
          </div>
          {isLoadingCrypto ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                minHeight: "400px",
              }}
            >
              <Spin size="large" className="custom-spin" />
            </div>
          ) : (
            <>
              {data.map((item, index) => (
                <Item
                  item={item}
                  method={method}
                  setSelectedItem={setSelectedItem}
                  key={index}
                  setDeposit={setDeposit}
                  setWithdraw={setWithdraw}
                  setExchange={setExchange}
                  setPrevWithdraw={setPrevWithdraw}
                  currencyOption={currencyOption}
                  rates={rates}
                  canAction={canAction}
                />
              ))}
            </>
          )}
        </div>
      </div>
      <ModalDetail
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        setDeposit={setDeposit}
        setWithdraw={setWithdraw}
        setPrevWithdraw={setPrevWithdraw}
        setExchange={setExchange}
        canAction={canAction}
        currencyOption={currencyOption}
        method={method}
        socket={socket}
        rates={rates}
      />
      <ModalDeposit selectedItem={deposit} setSelectedItem={setDeposit} />
      <ModalWithdraw
        selectedItem={withdraw}
        setSelectedItem={setWithdraw}
        prevSelectedItem={prevWithdraw}
        setPrevSelectedItem={setPrevWithdraw}
        setOpenModalSuccess={setOpenModalSuccess}
        setResponseWithdrawal={setResponseWithdrawal}
        refetchCryptoCurrency={refetchCryptoCurrency}
        refetchTotalAsset={refetchTotalAsset}
        socket={socket}
      />
      <ModalExchange
        selectedItem={exchange}
        setSelectedItem={setExchange}
        socket={socket}
        dataListCrypto={data}
      />
      <ModalSuccess
        openModalSuccess={openModalSuccess}
        setOpenModalSuccess={setOpenModalSuccess}
        responseWithdrawal={responseWithdrawal}
        setResponseWithdrawal={setResponseWithdrawal}
        socket={socket}
      />
    </div>
  );
};
